import { LanguageCode, Segmentation, SurveyIntroduction, SurveySegment, SurveySegmentation, SurveyTheme, SurveyType, SurveyTypes } from "../../../DTOs";
import { fieldValidation } from "../fieldValidation";
import { basicValidation } from "../basicValidation";
import { Result } from "../../../DTOs/Result";
import { languageValidation } from "../languageValidation";
import { getSegmentListKey } from "../../survey/segmentationHelpers";
import { assertNever } from "../../..";

export type SurveySegmentationListError = SegmentationListError |
    'invalid languagecode' | 'inconsistent value count' | 'inconsistent translation definition';


export type SegmentationListError = SegmentationError | 'duplicate segmentation name';

export type SegmentationError =
    'invalid data format' |
    'invalid value count' |
    'invalid segmentation value' |
    'duplicate segmentation value' |
    'invalid segmentation name';

export const surveyValidation = {
    survey:{
        id: (value: string) =>  basicValidation.resourceId.isValid(value),
        type: (value: SurveyType) => {
            return Object.values(SurveyTypes).find(v => v === value) !== undefined;
        },
        introductionText: validateIntroductionText,
        introduction:validateIntroduction,
        segmentationName: validateSegmentationName,
        segmentationValue: validateSegmentationValue,
        segmentationValues: validateSegmentationValue,
        segmentationIndex: (index:number) => basicValidation.validateInteger(index, 0, 1000),
        language:basicValidation.validateLanguageCode,
        translationLanguages:(translationLanguages:LanguageCode[]) =>
            languageValidation.validateLanguageCodeList(translationLanguages),

        oldTranslationsValidation:validateTranslations
    },
    
    surveyTheme:{
        index: (value: number) => basicValidation.validateInteger(value, 0, 100),
        name: (themeName: string) =>  basicValidation.resourceText.isValid(themeName, false, 200),
        // theme: (value:SurveyTheme) => {
        //     return value !== null && value !== undefined && typeof value === 'object'
        //         && fieldValidation.surveyTheme.name(value.name)
        //         && surveyQuestionObjectValidation fieldValidation.surveyQuestion.questionList(value.questions);
        // },
        // themeList:validateSurveyThemeList,
        languageCode:basicValidation.validateLanguageCode
    },

    surveyLink: {
        surveyId: (value: string) =>  basicValidation.resourceId.isValid(value),
        linkId: (value: string) =>  basicValidation.resourceId.isValid(value),
        segmentFilter: (value:SurveySegment[][]) => validateSurveySegmentListOfLists(value)
    },

    filter: {
        segmentSelections: validateSegmentFilter,
        hasDuplicateSegmentSelectionLists: hasDuplicateSegmentSelectionLists
    },

    surveyTemplate: {
        id: (value: string) =>  basicValidation.resourceId.isValid(value),
        enabled: basicValidation.validateBoolean,
        category: (value: string) =>  basicValidation.resourceText.isValid(value, true, 100),
        description: (value: string) =>  basicValidation.resourceText.isValid(value, false, 200),
        title:validateSurveyTitle,
        // themes:(value:SurveyTheme[]) => 
        //     validateSurveyThemeList(value) && 
        //     value.length > 0 &&
        //     value.find(t => t.questions.length===0)===undefined
    },

    surveyFileInvitation: {
        fileId: (value: string) =>  basicValidation.resourceId.isValid(value),
        surveyId: (value: string) =>  basicValidation.resourceId.isValid(value),
        subject: (value: string) => basicValidation.validateTextString(value, 1, 100),
        body: (value: string) => basicValidation.validateTextString(value, 1, 2000)
    },

    

    surveyAnswer: {
        id: (value: string) =>  basicValidation.resourceId.isValid(value),
        surveySegments: validateUniqueSurveySegmentList,
        value: (selections: string[]) => {
            if(!Array.isArray(selections))
                return false;
            if(new Set(selections).size !== selections.length)
                return false;
            return selections.every(s => basicValidation.resourceText.isValid(s, false, 100));
        },
        comment: (text?: string) => text === undefined ||  basicValidation.resourceText.isValid(text, true, 1000),
        email: (value: string) => basicValidation.isValidEmail(value, false),
        assignedAnswerIdOrNone: (id:string) =>  basicValidation.resourceId.isValidOrEmpty(id),

        segmentSelections:validateSegmentSelections
    },

    surveySegment:{
        validate:validateSurveySegment,
        validateUniqueList:validateUniqueSurveySegmentList,
        validateUniqueListOfLists:validateSurveySegmentListOfLists
    },

    surveySegmentation:{
        validateSurveySegmentationList,
        validateSegmentation,
        validateSegmentationValues,
        validateSegmentationValue,
        validateSegmentationName
    }
}


function validateSurveyTitle(text:string)
{
    return  basicValidation.resourceText.isValid(text, false, 100)
}

// function validateSurveyThemeList(values:SurveyTheme[]) {
//     return values !== null && values !== undefined && Array.isArray(values)
//         && values.find(v => !fieldValidation.surveyTheme.theme(v)) === undefined;
// }

function validateSegmentSelections(surveySegmentations:SurveySegmentation[], selections:SurveySegment[])
{
    // Selections and segmentations have same length
    if(surveySegmentations.length !== selections.length)
        return false;
        
    // All segmentations have a valid selection
    for(let i=0; i<surveySegmentations.length; i++){
        const segmentation = surveySegmentations[i];
        const selection = selections.find(s => s.name === segmentation.name);
        if(selection === undefined)
        {
            return false;
        }

        if(!segmentation.values.find(s => s === selection.value)){
            return false;
        }
    }
    return true;
}

function validateTranslations(surveyLanguage:LanguageCode, translationLanguages:LanguageCode[], introduction:SurveyIntroduction, segmentations:SurveySegmentation[], themes:SurveyTheme[]):Result
{
    if(!languageValidation.validateLanguageCode(surveyLanguage))
        return Result.fail('invalid surveyLanguage');

    if(!languageValidation.validateLanguageCodeList(translationLanguages))
        return Result.fail('invalid translationLanguages');

    if(translationLanguages.includes(surveyLanguage))
        return Result.fail('surveyLanguage in translationLanguages');

    if(!validateIntroduction(translationLanguages, introduction))
        return Result.fail('invalid introduction translation definition');

    // Segmentations are defined
    if(segmentations.length > 0){
        for(const s of segmentations){
            if(!languageValidation.validateLanguageCodeLists([translationLanguages, s.translations.map(st => st.languageCode)]))
                return Result.fail('invalid segmentation translation definition');
        }
    }

    if(themes.length > 0){
        for(const t of themes){
            if(!languageValidation.validateLanguageCodeLists([translationLanguages, t.translations.map(tt => tt.languageCode)]))
                return Result.fail('invalid theme translation definition');

            for(const q of t.questions){
                if(!languageValidation.validateLanguageCodeLists([translationLanguages, q.translations.map(qt => qt.languageCode)]))
                    return Result.fail('invalid question translation definition');

                if(q.type === 'default')
                {
                    // do nothing
                }
                else if(q.type === 'custom'){
                    const options = q.options || [];
                    for(const option of options){
                        const optionTranslations = option.translations || [];
                        if(!languageValidation.validateLanguageCodeLists([translationLanguages, optionTranslations.map(ot => ot.languageCode)]))
                            return Result.fail('invalid question option translation definition');
                    }
                }
                else if(q.type === 'multiple-choice'){
                    const options = q.options || [];
                    for(const option of options){
                        const optionTranslations = option.translations || [];
                        if(!languageValidation.validateLanguageCodeLists([translationLanguages, optionTranslations.map(ot => ot.languageCode)]))
                            return Result.fail('invalid question option translation definition');
                    }
                }
                else
                {
                    assertNever(q);
                    return Result.fail('invalid question type');
                }
            }
        }
    }
    return Result.succeed();
}

function validateIntroduction(translationLanguages:LanguageCode[], introduction:SurveyIntroduction)
{
    if(introduction === null || introduction === undefined)
        return false;

    if(!fieldValidation.survey.introductionText(introduction.text))
        return false;

    if(!languageValidation.validateLanguageCodeLists([translationLanguages, introduction.translations.map(t => t.languageCode)]))
        return false;

    return introduction.translations.every(t => fieldValidation.survey.introductionText(t.text));
}

function validateIntroductionText(introductionText:string)
{
    return  basicValidation.resourceText.isValid(introductionText, false, 2000)
}

function validateSurveySegmentListOfLists(selectionsList: SurveySegment[][]) {
    if (selectionsList === null || selectionsList === undefined) return false;
    if (!Array.isArray(selectionsList)) return false;
    for(const l of selectionsList){
        if(!validateUniqueSurveySegmentList(l))
            return false;
    }
    return true;
}

function validateSegmentFilter(
    surveySegmentations: SurveySegmentation[],
    selectionsList: SurveySegment[][]
)
{
    if(!Array.isArray(selectionsList))
        return false;

    for(const selections of selectionsList)
    {
        if(!validateUniqueSurveySegmentList(selections))
            return false;

        if(!validateSegmentSelections(surveySegmentations, selections))
            return false;
    }

    return true;
}

function hasDuplicateSegmentSelectionLists(selectionsList: SurveySegment[][])
{
    if(!validateSurveySegmentListOfLists(selectionsList))
        return false;

    const keys = selectionsList.map(selectionList => {
        const clonedList = selectionList.map(s => ({ ...s }));
        return getSegmentListKey(clonedList);
    });

    return !basicValidation.stringValuesAreUnique(keys);
}

function validateSurveySegmentationList(translationLanguages:LanguageCode[], list:SurveySegmentation[]):SurveySegmentationListError | 'ok'
{
    if(list === null || list === undefined || !Array.isArray(list))
        return 'invalid data format';

    if(list.length === 0)
        return 'ok';

    for(const s of list){
        if(!s)
            return 'invalid data format';

        if(!Array.isArray(s.translations))
            return 'invalid data format';

        for(const tr of s.translations){
            if(!tr)
                return 'invalid data format';

            if(!tr.languageCode)
                return 'invalid data format';

            if(!languageValidation.validateLanguageCode(tr.languageCode))
                return 'invalid languagecode';

            const segmentationValidation = validateSegmentation(tr);
            if(segmentationValidation !== 'ok')
                return segmentationValidation;
        }

        if(!languageValidation.validateLanguageCodeLists([s.translations.map(t => t.languageCode), translationLanguages]))
            return 'inconsistent translation definition';
    }

    const defaultValueValidation = validateSegmentationList(list);
    if(defaultValueValidation !== 'ok')
        return defaultValueValidation;

    for(const tl of translationLanguages){
        const translatedSegmentations:Segmentation[] = [];
        for(const seg of list){
            const translation = seg.translations.find(t => t.languageCode === tl);
            if(!translation)
                return 'inconsistent translation definition';

            if(translation.values.length !== seg.values.length)
                return 'inconsistent value count';

            translatedSegmentations.push({
                name:translation.name,
                values:translation.values
            })
        }
        const listValidation = validateSegmentationList(translatedSegmentations);
        if(listValidation !== 'ok')
            return listValidation;
    }

    return 'ok';
}

function validateSegmentationList(list:Segmentation[]):SegmentationListError | 'ok'
{
    if(!Array.isArray(list))
        return 'invalid data format';
    
    for(const s of list)
    {
        const validation = validateSegmentation(s);
        if(validation !== 'ok')
            return validation;
    }

    if(!basicValidation.stringValuesAreUnique(list.map(s => s.name)))
        return 'duplicate segmentation name';

    return 'ok';
}

function validateSegmentation(seg:Segmentation):SegmentationError | 'ok'
{
    if(!seg)
        return 'invalid data format';

    const nameValidation = validateSegmentationName(seg.name);
    if(!nameValidation.success)
        return 'invalid segmentation name';

    const valuesValidation = validateSegmentationValues(seg.values);
    if(valuesValidation !== 'ok')
        return valuesValidation;

    return 'ok';
}

function validateSegmentationValues(values:string[]):SegmentationError | 'ok'
{
    if(!Array.isArray(values))
        return 'invalid data format';
    if(values.length < 2)
        return 'invalid value count';
    if(!values.every(v => validateSegmentationValue(v).success))
        return 'invalid segmentation value';
    if(!basicValidation.arrayStringsAreUnique(values))
        return 'duplicate segmentation value';

    return 'ok';
}

function validateUniqueSurveySegmentList(selections: SurveySegment[]) {
    return selections !== null && selections !== undefined
        && Array.isArray(selections)
        && selections.every(s => validateSurveySegment(s))
        // Never allow name+value duplicates
        && basicValidation.stringValuesAreUnique(
            selections.map(s => s.name + '$$' + s.value)
        )
        && basicValidation.arrayStringsAreUnique(selections.map(s => s.name));
}

function validateSurveySegment(selection: SurveySegment) {
    if (selection === null || selection === undefined)
        return false;
    
    return validateSegmentationName(selection.name).success
        && validateSegmentationValue(selection.value).success;
}

function validateSegmentationValue(value:string)
{ 
    return  basicValidation.resourceText.isValid(value, false, 50)
        ? Result.succeed()
        : Result.fail('invalid segmentation value');
}

function validateSegmentationName(name:string) 
{ 
    return  basicValidation.resourceText.isValid(name, false, 50)
        ? Result.succeed()
        : Result.fail('invalid segmentation name');
}
